import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TYPES = ["group", "business_unit", "function", "department", "site", "team"];

export default function AdminOrg() {
  const { tenantId } = useAuth();
  const [nodes, setNodes] = useState([]);
  const [form, setForm] = useState({ name: "", node_type: "business_unit", parent_id: "root" });

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/org-nodes`).then(({ data }) => setNodes(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  const create = async () => {
    try {
      await api.post(`/tenants/${tenantId}/org-nodes`, {
        ...form,
        parent_id: form.parent_id === "root" ? null : form.parent_id,
      });
      toast.success("Org node added");
      setForm({ name: "", node_type: "business_unit", parent_id: "root" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const tree = (parent = null, depth = 0) =>
    nodes
      .filter((n) => (n.parent_id || null) === parent)
      .flatMap((n) => [
        <div key={n.id} className="flex items-center justify-between gap-4 px-5 py-3" data-testid={`org-node-${n.id}`}>
          <div className="flex items-center gap-3" style={{ paddingLeft: depth * 20 }}>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{n.node_type}</span>
            <span className="text-sm">{n.name}</span>
          </div>
          <Button
            data-testid={`delete-org-node-${n.id}`}
            size="sm"
            variant="ghost"
            className="rounded-none text-[10px] uppercase tracking-[0.15em] text-destructive"
            onClick={async () => {
              try {
                await api.delete(`/tenants/${tenantId}/org-nodes/${n.id}`);
                load();
              } catch (e) {
                toast.error(errMsg(e));
              }
            }}
          >
            Remove
          </Button>
        </div>,
        ...tree(n.id, depth + 1),
      ]);

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-org-header"
        eyebrow="Administration"
        title="Org hierarchy builder"
        description="Org scope is one of five permission dimensions. Each tenant configures its own levels."
      />
      <div className="panel p-6">
        <p className="label-caps mb-5">Add node</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="space-y-2">
            <Label className="label-caps">Name</Label>
            <Input data-testid="org-name-input" className="rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Type</Label>
            <Select value={form.node_type} onValueChange={(v) => setForm({ ...form, node_type: v })}>
              <SelectTrigger data-testid="org-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Parent</Label>
            <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
              <SelectTrigger data-testid="org-parent-select" className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="root">No parent (root)</SelectItem>
                {nodes.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button data-testid="create-org-node-button" className="h-10 w-full rounded-none text-[10px] uppercase tracking-[0.18em]" disabled={!form.name} onClick={create}>
              Add node
            </Button>
          </div>
        </div>
      </div>
      <div className="panel divide-y divide-border" data-testid="org-tree">
        {nodes.length ? tree() : <EmptyState testid="org-empty" title="No org nodes" message="Define the hierarchy this engagement uses." />}
      </div>
    </div>
  );
}
